import 'dayjs/plugin/timezone';

import type { ConfigType, Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import { formatDate } from '@/utils/dateUtils';
import { getUserTimezone } from '@/utils/timezone';

interface DateRange {
  start?: ConfigType;
  end?: ConfigType;
}

export function useDateUtils() {
  function convertTimestampToUserTimezone(timestamp: ConfigType | Dayjs, format: string) {
    if (timestamp) {
      const tz = getUserTimezone();
      return dayjs.isDayjs(timestamp)
        ? timestamp.tz(tz).format(format)
        : dayjs(timestamp).tz(tz).format(format);
    }
    return '';
  }

  function getFormattedDateFromRange(range: DateRange | null | undefined) {
    const endYear = formatDate(range?.end, 'YYYY');
    const isCurrentYear = endYear === formatDate(new Date(), 'YYYY');
    const isSameYear = formatDate(range?.start, 'YYYY') === endYear;
    let endDateFormat;

    if (formatDate(range?.start, 'MMM, YYYY') === formatDate(range?.end, 'MMM, YYYY')) {
      endDateFormat = isSameYear && isCurrentYear ? 'DD' : 'DD, YYYY';
    } else {
      endDateFormat = isSameYear && isCurrentYear ? 'MMM DD' : 'MMM DD, YYYY';
    }

    const endDate = formatDate(range?.end, endDateFormat);
    const isSameDay = endDate === formatDate(range?.start, isCurrentYear ? 'DD' : 'DD, YYYY');
    const startDateFormat = isSameYear && (isCurrentYear || !isSameDay) ? 'MMM DD' : 'MMM DD, YYYY';
    const startDate = formatDate(range?.start, startDateFormat);

    if (isSameDay) {
      // If the end date is the same day as the start date, we don't need to show the end date
      return `${startDate}`;
    }
    return `${startDate} - ${endDate}`;
  }

  return { convertTimestampToUserTimezone, getFormattedDateFromRange };
}
